/**
 * User Detail Page
 *
 * Full-page view for managing a single user:
 * - Registration info & session history
 * - AI usage metrics
 * - App permissions (toggles)
 * - Agent assignments (multiple per user, like apps)
 * - Tenant memberships (superadmin only)
 * - Support tickets
 * - Danger zone (block/archive/delete)
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUserDetail,
  updateUserPermissions,
  updateUserVoice,
  updateUserPreferredLanguage,
  getUserAgents,
  updateUserAgent,
  blockUser,
  unblockUser,
  deleteUser,
  resetUserPassword,
  listTenants,
  getUserTenants,
  addUserToTenant,
  removeUserFromTenant,
  getMyAdminTenants,
  addUserToTenantScoped,
  removeUserFromTenantScoped,
  updateUserRoleScoped,
  listRoles,
  type UserDetail,
  type AppPermissions,
  type UserAgentAssignment,
  type Tenant,
} from '@/services/adminApi';
import type { Role } from '@/types/roles';
import { UserStatus } from '@/types/user';
import PasswordField from './PasswordField';

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { user: currentUser } = useAuth();

  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [userAgents, setUserAgents] = useState<UserAgentAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  // Tenant management state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [userTenantMemberships, setUserTenantMemberships] = useState<Record<string, string>>({});
  const isSuperadmin = currentUser?.is_superadmin === true;

  // ACL roles from roles table
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const isTenantAdmin = !isSuperadmin && currentUser?.role === 'admin';

  // DEBUG: Log superadmin status
  console.log('[UserDetailPage] currentUser:', currentUser);
  console.log('[UserDetailPage] isSuperadmin:', isSuperadmin);
  console.log('[UserDetailPage] tenants:', tenants);

  // Limitation prompt editing state (per agent)
  const [editingPrompt, setEditingPrompt] = useState<number | null>(null);
  const [promptDraft, setPromptDraft] = useState('');

  // Danger zone confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Password reset modal state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password validation
  const passwordsMatch = useMemo(
    () => newPassword === confirmPassword,
    [newPassword, confirmPassword]
  );
  const passwordValid = useMemo(
    () => newPassword.length >= 8,
    [newPassword]
  );
  const canResetPassword = passwordValid && passwordsMatch;

  useEffect(() => {
    if (userId) {
      loadData(parseInt(userId));
    }
  }, [userId]);

  // Load tenant data when admin status is confirmed
  useEffect(() => {
    if (userId && (isSuperadmin || isTenantAdmin)) {
      loadTenantData(parseInt(userId));
    }
  }, [userId, isSuperadmin, isTenantAdmin]);

  const loadData = async (id: number) => {
    setIsLoading(true);
    setError('');
    try {
      const [userDetail, agentsData] = await Promise.all([
        getUserDetail(id),
        getUserAgents(id)
      ]);

      setDetail(userDetail);
      setUserAgents(agentsData.agents);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Load tenants and roles separately (avoids timing issues with currentUser)
  const loadTenantData = async (id: number) => {
    try {
      // Load ACL roles
      try {
        const rolesData = await listRoles();
        setAvailableRoles(rolesData.roles);
      } catch {
        console.warn('Failed to load roles');
      }

      let tenantsResult: { id: string; name: string; domain: string; user_count?: number }[];

      if (isSuperadmin) {
        // Superadmins see all tenants
        const tenantsData = await listTenants();
        tenantsResult = tenantsData.tenants;
      } else {
        // Tenant admins see only their admin tenants
        const myTenants = await getMyAdminTenants();
        tenantsResult = myTenants.tenants.map(t => ({
          id: t.tenant_id,
          name: t.name,
          domain: t.domain,
        }));
      }

      const userTenantsData = await getUserTenants(id);

      setTenants(tenantsResult as Tenant[]);
      // Build membership map: tenant_id -> role
      const memberships: Record<string, string> = {};
      for (const m of userTenantsData.tenants) {
        memberships[m.tenant_id] = m.role;
      }
      setUserTenantMemberships(memberships);
    } catch (err: unknown) {
      console.error('Failed to load tenant data:', err);
    }
  };

  // Permission toggle handler
  const handlePermissionToggle = async (key: keyof AppPermissions) => {
    if (!detail || !userId) return;
    setSaving(key);
    try {
      const newValue = !detail.user.app_permissions[key];
      await updateUserPermissions(parseInt(userId), { [key]: newValue });
      setDetail({
        ...detail,
        user: {
          ...detail.user,
          app_permissions: { ...detail.user.app_permissions, [key]: newValue }
        }
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update permission';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Voice toggle handler
  const handleVoiceToggle = async () => {
    if (!detail || !userId) return;
    setSaving('voice');
    try {
      const newValue = !detail.user.voice_enabled;
      await updateUserVoice(parseInt(userId), newValue);
      setDetail({
        ...detail,
        user: { ...detail.user, voice_enabled: newValue }
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update voice setting';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Preferred language handler
  const handleLanguageChange = async (language: string) => {
    if (!detail || !userId) return;
    setSaving('language');
    try {
      await updateUserPreferredLanguage(parseInt(userId), language);
      setDetail({
        ...detail,
        user: { ...detail.user, preferred_language: language }
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update language';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Agent toggle handler (like app permissions)
  const handleAgentToggle = async (agentId: number) => {
    if (!userId) return;
    const agent = userAgents.find(a => a.agent_id === agentId);
    if (!agent) return;

    setSaving(`agent-${agentId}`);
    try {
      const newEnabled = !agent.enabled;
      await updateUserAgent(parseInt(userId), {
        agent_id: agentId,
        enabled: newEnabled
      });
      // Update local state
      setUserAgents(userAgents.map(a =>
        a.agent_id === agentId ? { ...a, enabled: newEnabled } : a
      ));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle agent';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Save agent limitation prompt
  const handleSaveLimitationPrompt = async (agentId: number) => {
    if (!userId) return;
    setSaving(`prompt-${agentId}`);
    try {
      await updateUserAgent(parseInt(userId), {
        agent_id: agentId,
        limitation_prompt: promptDraft || undefined
      });
      // Update local state
      setUserAgents(userAgents.map(a =>
        a.agent_id === agentId ? { ...a, limitation_prompt: promptDraft || null } : a
      ));
      setEditingPrompt(null);
      setPromptDraft('');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save prompt';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Block/unblock user
  const handleToggleBlock = async () => {
    if (!detail || !userId) return;
    setSaving('block');
    try {
      if (detail.user.status === UserStatus.BLOCKED) {
        await unblockUser(parseInt(userId));
        setDetail({
          ...detail,
          user: { ...detail.user, status: UserStatus.ACTIVE }
        });
      } else {
        await blockUser(parseInt(userId));
        setDetail({
          ...detail,
          user: { ...detail.user, status: UserStatus.BLOCKED }
        });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Delete user
  const handleDelete = async () => {
    if (!userId) return;
    setSaving('delete');
    try {
      await deleteUser(parseInt(userId));
      navigate('/admin');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      alert(errorMessage);
    } finally {
      setSaving(null);
      setConfirmDelete(false);
    }
  };

  // Reset password
  const handlePasswordReset = async () => {
    if (!userId || !canResetPassword) return;
    setSaving('password');
    try {
      await resetUserPassword(parseInt(userId), newPassword);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      alert(t('userDetail.passwordResetSuccess'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Toggle tenant membership
  const handleTenantToggle = async (tenantId: string) => {
    if (!userId) return;

    const isAssigned = tenantId in userTenantMemberships;
    setSaving(`tenant-${tenantId}`);
    try {
      if (isAssigned) {
        // Remove from tenant
        if (isSuperadmin) {
          await removeUserFromTenant(tenantId, parseInt(userId));
        } else {
          await removeUserFromTenantScoped(parseInt(userId), tenantId);
        }
        setUserTenantMemberships(prev => {
          const next = { ...prev };
          delete next[tenantId];
          return next;
        });
      } else {
        // Add to tenant
        if (isSuperadmin) {
          await addUserToTenant(tenantId, parseInt(userId), 'user');
        } else {
          await addUserToTenantScoped(parseInt(userId), 'user', tenantId);
        }
        setUserTenantMemberships(prev => ({ ...prev, [tenantId]: 'user' }));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant membership';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Change user's role within a tenant
  const handleTenantRoleChange = async (tenantId: string, role: string) => {
    if (!userId) return;

    setSaving(`tenant-role-${tenantId}`);
    try {
      if (isSuperadmin) {
        // Superadmin uses the direct tenant endpoint (UPSERT updates role)
        await addUserToTenant(tenantId, parseInt(userId), role);
      } else {
        await updateUserRoleScoped(parseInt(userId), role, tenantId);
      }
      setUserTenantMemberships(prev => ({ ...prev, [tenantId]: role }));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant role';
      alert(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse user agent for display
  const parseUserAgent = (ua: string | null) => {
    if (!ua) return 'Unknown device';
    if (ua.includes('Chrome')) return `Chrome on ${ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'Mac' : 'Linux'}`;
    if (ua.includes('Firefox')) return `Firefox on ${ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'Mac' : 'Linux'}`;
    if (ua.includes('Safari')) return `Safari on ${ua.includes('iPhone') ? 'iOS' : 'Mac'}`;
    return ua.substring(0, 50);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || t('common.userNotFound')}
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          &larr; {t('navigation.backToUsers')}
        </button>
      </div>
    );
  }

  const { user, sessions, usage, tickets, ticket_count } = detail;
  const isProtected = user.username === 'root' || (user.username === 'admin' && currentUser?.username !== 'root');

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin')}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <span>&larr;</span> {t('navigation.backToUsers')}
        </button>
        {!isProtected && (
          <div className="flex gap-2">
            <button
              onClick={handleToggleBlock}
              disabled={saving === 'block'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                user.status === 'blocked'
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              {saving === 'block' ? '...' : user.status === 'blocked' ? t('userDetail.unblock') : t('userDetail.block')}
            </button>
          </div>
        )}
      </div>

      {/* User Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {user.full_name?.charAt(0) || user.username.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">{user.full_name || user.username}</h1>
            <div className="flex items-center gap-2 mt-1 text-gray-600">
              <span>@{user.username}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {user.role}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                user.status === 'active' ? 'bg-green-100 text-green-700' :
                user.status === 'blocked' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {user.status}
              </span>
            </div>
            <div className="text-gray-500 mt-1">{user.email}</div>
          </div>
        </div>
      </div>

      {/* Info Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Registration Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userDetail.registrationInfo')}</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('userDetail.created')}</dt>
              <dd className="text-gray-900">{formatDate(user.created_at)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('userDetail.lastLogin')}</dt>
              <dd className="text-gray-900">{formatDate(user.last_login)}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-gray-500">{t('userDetail.preferredLanguage')}</dt>
              <dd>
                <select
                  value={user.preferred_language || 'en'}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  disabled={saving === 'language'}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="it">Italiano</option>
                </select>
              </dd>
            </div>
          </dl>
        </div>

        {/* Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userStats.activity')}</h2>
          <dl className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-gray-500 text-sm">{t('userStats.tasks')}</dt>
              <dd className="text-xl font-semibold text-gray-900">{usage.tasks_total} <span className="text-sm font-normal text-gray-500">({usage.tasks_completed} {t('userStats.completed')})</span></dd>
            </div>
            <div>
              <dt className="text-gray-500 text-sm">{t('userStats.iterations')}</dt>
              <dd className="text-xl font-semibold text-gray-900">{usage.iterations_total}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-sm">{t('userStats.contacts')}</dt>
              <dd className="text-xl font-semibold text-gray-900">{usage.contacts_total}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-sm">{t('userStats.calendar')}</dt>
              <dd className="text-xl font-semibold text-gray-900">{usage.events_total}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Session History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userDetail.sessionHistory')}</h2>
        {sessions.length === 0 ? (
          <p className="text-gray-500">{t('userDetail.noSessionHistory')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">{t('userDetail.status')}</th>
                  <th className="pb-2">{t('userDetail.device')}</th>
                  <th className="pb-2">{t('userDetail.ipAddress')}</th>
                  <th className="pb-2">{t('userDetail.lastActive')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice(0, 10).map((session) => (
                  <tr key={session.id} className="border-b last:border-0">
                    <td className="py-2">
                      <span className={`inline-flex items-center gap-1 ${
                        session.is_active ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          session.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}></span>
                        {session.is_active ? t('users.active') : 'Expired'}
                      </span>
                    </td>
                    <td className="py-2 text-gray-700">{parseUserAgent(session.user_agent)}</td>
                    <td className="py-2 text-gray-600 font-mono text-xs">{session.ip_address || 'N/A'}</td>
                    <td className="py-2 text-gray-500">{formatDate(session.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* App Permissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userDetail.appPermissions')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'calendar' as const, icon: '📅', label: t('userDetail.calendar') },
            { key: 'email' as const, icon: '📧', label: t('userDetail.email') },
            { key: 'knowledge_base' as const, icon: '📚', label: t('userDetail.kb') },
            { key: 'cogito' as const, icon: '🧠', label: t('userDetail.cogito') },
            { key: 'tasks' as const, icon: '✅', label: t('userDetail.tasks') },
            { key: 'disk' as const, icon: '💾', label: t('userDetail.disk') },
            { key: 'people' as const, icon: '👥', label: t('userDetail.people') },
            { key: 'chat' as const, icon: '💬', label: t('userDetail.chat') },
            { key: 'contractors' as const, icon: '🔧', label: t('userDetail.contractors') },
          ].map(({ key, icon, label }) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <span>{icon}</span>
                <span className="text-gray-700">{label}</span>
              </div>
              <button
                onClick={() => handlePermissionToggle(key)}
                disabled={saving === key}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  user.app_permissions[key] ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    user.app_permissions[key] ? 'left-7' : 'left-1'
                  }`}
                ></span>
              </button>
            </div>
          ))}
          {/* Voice UI Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span>🎤</span>
              <span className="text-gray-700">{t('userDetail.voiceUI')}</span>
            </div>
            <button
              onClick={handleVoiceToggle}
              disabled={saving === 'voice'}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                user.voice_enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  user.voice_enabled ? 'left-7' : 'left-1'
                }`}
              ></span>
            </button>
          </div>
        </div>
      </div>

      {/* Agent Assignments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('userDetail.agentAssignments')}</h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('userDetail.agentAssignmentsDescription')}
        </p>
        <div className="space-y-4">
          {userAgents.length === 0 ? (
            <p className="text-gray-500">{t('userDetail.noAgentsAvailable')}</p>
          ) : (
            userAgents.map((agent) => (
              <div
                key={agent.agent_id}
                className={`p-4 rounded-lg border ${
                  agent.enabled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🤖</span>
                      <span className="font-medium text-gray-900">{agent.agent_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        agent.agent_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {agent.agent_status}
                      </span>
                    </div>
                    {agent.agent_description && (
                      <p className="text-sm text-gray-500 mt-1">{agent.agent_description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleAgentToggle(agent.agent_id)}
                    disabled={saving === `agent-${agent.agent_id}`}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      agent.enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        agent.enabled ? 'left-7' : 'left-1'
                      }`}
                    ></span>
                  </button>
                </div>

                {/* Limitation Prompt */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{t('userDetail.limitationPrompt')}</span>
                    {editingPrompt !== agent.agent_id && (
                      <button
                        onClick={() => {
                          setEditingPrompt(agent.agent_id);
                          setPromptDraft(agent.limitation_prompt || '');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {t('userDetail.edit')}
                      </button>
                    )}
                  </div>
                  {editingPrompt === agent.agent_id ? (
                    <div className="space-y-2">
                      <textarea
                        value={promptDraft}
                        onChange={(e) => setPromptDraft(e.target.value)}
                        placeholder={t('userDetail.customInstructionsPlaceholder')}
                        className="w-full p-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveLimitationPrompt(agent.agent_id)}
                          disabled={saving === `prompt-${agent.agent_id}`}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          {saving === `prompt-${agent.agent_id}` ? t('userDetail.saving') : t('userDetail.save')}
                        </button>
                        <button
                          onClick={() => {
                            setEditingPrompt(null);
                            setPromptDraft('');
                          }}
                          className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {agent.limitation_prompt || t('userDetail.noneUsesAgentDefault')}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Tenant Memberships */}
      {(isSuperadmin || isTenantAdmin) && tenants.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('userDetail.tenantMemberships')}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {t('userDetail.tenantMembershipsDescription')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tenants.map((tenant) => {
              const isAssigned = tenant.id in userTenantMemberships;
              const memberRole = userTenantMemberships[tenant.id] || 'user';
              const isSavingThis = saving === `tenant-${tenant.id}` || saving === `tenant-role-${tenant.id}`;
              return (
                <div
                  key={tenant.id}
                  className={`flex flex-col p-4 rounded-lg border transition-colors ${
                    isAssigned
                      ? 'border-blue-200 bg-blue-50/50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {tenant.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {tenant.domain}
                      </div>
                      {tenant.user_count !== undefined && (
                        <div className="text-xs text-gray-400 mt-1">
                          {tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleTenantToggle(tenant.id)}
                      disabled={isSavingThis}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        isAssigned ? 'bg-blue-600' : 'bg-gray-300'
                      } ${isSavingThis ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          isAssigned ? 'left-7' : 'left-1'
                        }`}
                      ></span>
                    </button>
                  </div>
                  {/* Role dropdown when assigned */}
                  {isAssigned && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <select
                        value={memberRole}
                        onChange={(e) => handleTenantRoleChange(tenant.id, e.target.value)}
                        disabled={isSavingThis}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {availableRoles.length > 0 ? (
                          availableRoles
                            .filter(r => isSuperadmin || r.name !== 'superadmin')
                            .map(r => (
                              <option key={r.id} value={r.name}>{r.display_name}</option>
                            ))
                        ) : (
                          <>
                            <option value="user">{t('users.user')}</option>
                            <option value="admin">{t('users.admin')}</option>
                          </>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Support Tickets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('userDetail.supportTickets')} ({ticket_count})</h2>
        </div>
        {tickets.length === 0 ? (
          <p className="text-gray-500">{t('userDetail.noSupportTickets')}</p>
        ) : (
          <div className="space-y-2">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <span className="text-gray-900">#{ticket.id} - {ticket.subject}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                    ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                    ticket.status === 'closed' ? 'bg-gray-100 text-gray-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {ticket.status}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{formatDate(ticket.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Password Management */}
      {!isProtected && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('userDetail.passwordManagement')}</h2>
          <button
            onClick={() => setShowPasswordReset(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            {t('userDetail.forcePasswordReset')}
          </button>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('userDetail.resetPasswordFor')} {user.username}
              </h3>
              <button
                onClick={() => {
                  setShowPasswordReset(false);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <PasswordField
                label={t('userDetail.newPassword')}
                value={newPassword}
                onChange={setNewPassword}
                placeholder={t('userDetail.newPassword')}
                required
                showGenerator
                showStrength
                error={
                  newPassword && !passwordValid
                    ? t('createUserForm.passwordMustBe8Chars')
                    : undefined
                }
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('userDetail.confirmPasswordLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('userDetail.confirmNewPassword')}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    confirmPassword && !passwordsMatch ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {confirmPassword && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${
                    passwordsMatch ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {passwordsMatch ? (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {t('createUserForm.passwordsMatch')}
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        {t('createUserForm.passwordsDoNotMatch')}
                      </>
                    )}
                  </p>
                )}
              </div>

              <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-yellow-800">
                  {t('userDetail.passwordResetWarning')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowPasswordReset(false);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePasswordReset}
                disabled={!canResetPassword || saving === 'password'}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  canResetPassword && saving !== 'password'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving === 'password' ? t('userDetail.resetting') : t('userDetail.resetPassword')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      {!isProtected && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-4">{t('userDetail.dangerZone')}</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleToggleBlock}
              disabled={saving === 'block'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                user.status === 'blocked'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
            >
              {saving === 'block' ? '...' : user.status === 'blocked' ? `✓ ${t('userDetail.unblockUser')}` : `🚫 ${t('userDetail.blockUser')}`}
            </button>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                🗑️ {t('userDetail.deletePermanently')}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-red-700 text-sm">{t('userDetail.areYouSure')}</span>
                <button
                  onClick={handleDelete}
                  disabled={saving === 'delete'}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-700 text-white hover:bg-red-800 transition-colors"
                >
                  {saving === 'delete' ? t('userDetail.deleting') : t('userDetail.yesDelete')}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
